<script setup lang="ts">

import {computed, ref, watch} from "vue";
import axios from 'axios';
import BaseSpinner from '@/core/components/base/spinner/BaseSpinner.vue';
import useSwr from "@/core/composables/swr/useSwr";
import {contentfulService} from "@/container";
import contentfulHelper from "@/core/helpers/contentfulHelper";
import {cacheKey} from "@/lib/cache";
import RichTextRenderer from "contentful-rich-text-vue-renderer";
import BaseFlexSpinner from "@/core/components/base/spinner/BaseFlexSpinner.vue";
import {Accordion, AccordionItem, AccordionContent, AccordionTrigger} from "@/core/components/ui/accordion";

const {data: entries, isLoading: entriesLoading} = useSwr(
    () => cacheKey('/contentful/welcome/entries'),
    () => contentfulService().simpleContents('welcome', 3),
    contentfulService().swrConfig()
);

const isLoading = ref(false);
const hasError = ref(false);
const open = ref(false);
const youtubeTutorials = ref<any[]>([]);
const youtubeWebinars = ref<any[]>([]);
const tutosPlaylistId = 'PLsBHFpvnM0qIIFxMb9zQfxUyDwDxla2zU';
const webinarPlaylistId = 'PLsBHFpvnM0qIvzdq_zS-rtUn5dVK7Iy5i';
const youtubeApiKey = import.meta.env.VITE_YOUTUBE_API_KEY;

/**
 * Get team guide content from Contentful.
 */
const content = computed(() => {
    return entries.value?.find(
        entry => entry.metadata.tags.find(tag => tag.sys.id === 'teamGuide') !== undefined
    )?.fields;
});

/**
 * Get the list of videos from YouTube.
 */
const fetchVideos = (() => {
    axios.get('https://youtube.googleapis.com/youtube/v3/playlistItems?part=snippet&playlistId=' + tutosPlaylistId + '&key=' + youtubeApiKey + '&maxResults=1337')
        .then(response => {
            youtubeTutorials.value = response.data.items;

            return axios.get('https://youtube.googleapis.com/youtube/v3/playlistItems?part=snippet&playlistId=' + webinarPlaylistId + '&key=' + youtubeApiKey + '&maxResults=1337');
        })
        .then(response => {
            youtubeWebinars.value = response.data.items;
        })
        .catch(() => {
            hasError.value = true;
        })
        .finally(() => {
            isLoading.value = false;
        });
});

/**
 * Watch accordion open state, and fetch videos if not done yet.
 */
watch(open, () => {
    if (open.value && youtubeTutorials.value.length === 0 && youtubeWebinars.value.length === 0) {
        fetchVideos();
    }
});
</script>

<template>
  <div class="w-full flex flex-col justify-center items-center md:columns-2">
    <div
      v-if="entriesLoading"
      class="h-screen flex items-center"
    >
      <BaseSpinner />
    </div>
    <div v-else-if="!entriesLoading" class="flex flex-col flex-1 px-4">
      <RichTextRenderer
        v-if="content"
        :document="content.content"
        :node-renderers="contentfulHelper.renderNodes()"
      />
    </div>
    <div class="flex flex-row items-start w-full p-4 pb-8 bg-white">
      <Accordion ref="accordionRef" type="single" collapsible class="w-full">
        <AccordionItem value="item" :open="open" @update:open="open = $event">
          <AccordionTrigger class="bg-white flex py-0">
            <div class="flex flex-row items-start w-full p-4">
              <h2>En savoir plus</h2>
            </div>
          </AccordionTrigger>
          <AccordionContent>
            <div
              v-if="hasError"
              class="h-screen flex items-center"
            >
              <p>Les videos n'ont pas pu être chargées, veuillez réessayer plus tard.</p>
            </div>
            <div v-if="isLoading">
              <BaseFlexSpinner />
            </div>
            <div
              v-else
              class="w-full flex flex-wrap justify-center items-center md:columns-2"
            >
              <div
                v-for="(item, index) in youtubeTutorials"
                :key="index"
                class="mt-12 flex flex-col items-center"
              >
                <p class="font-bold text-xl text-center text-purple-900 max-w-lg h-16">
                  {{ (index + 1) }}. {{ item.snippet.title }}
                </p>
                <iframe
                  :src="'https://www.youtube.com/embed/' + item.snippet.resourceId.videoId + '?rel=0&autoplay=0&showinfo=0&controls=0'"
                  width="600"
                  height="315"
                  frameborder="0"
                  class="m-2"
                />
              </div>
              <div
                v-for="(item, index) in youtubeWebinars"
                :key="index + youtubeTutorials.length"
                class="mt-12 flex flex-col items-center"
              >
                <p class="font-bold text-xl text-center text-purple-900 max-w-lg h-16">
                  {{ ((index + youtubeTutorials.length) + 1) }}. {{ item.snippet.title }}
                </p>
                <iframe
                  :src="'https://www.youtube.com/embed/' + item.snippet.resourceId.videoId + '?rel=0&autoplay=0&showinfo=0&controls=0'"
                  width="600"
                  height="315"
                  frameborder="0"
                  class="m-2"
                />
              </div>
            </div>
          </AccordionContent>
        </AccordionItem>
      </Accordion>
    </div>
  </div>
</template>

<style scoped>

</style>